import { api } from "@/lib/apiClient";

export type InvoiceApprovalStatus =
  | "offen"
  | "in_klaerung"
  | "warte_zweite_freigabe"
  | "freigegeben"
  | "abgelehnt";

export type InvoiceApprovalScope = "mine" | "offen" | "klaerung" | "zweite" | "erledigt" | "alle";

export interface AssignmentSuggestion {
  login?: string | null;
  name?: string | null;
  kuerzel?: string | null;
  kuerzels?: string[] | null;
  reason?: string | null;
  alternatives?: Array<{ login?: string | null; name?: string | null }>;
}

export interface InvoiceApprovalSummary {
  id?: number;
  status: InvoiceApprovalStatus;
  assignee_login?: string | null;
  assignee_name?: string | null;
  suggestion_kuerzel?: string | null;
  suggestion_reason?: string | null;
  second_assignee_login?: string | null;
  second_assignee_name?: string | null;
  four_eyes_required?: boolean;
  participants?: InvoiceApprovalParticipant[];
  decision_note?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  assigned_at?: string | null;
}

export interface InvoiceApprovalParticipant {
  login: string;
  name?: string | null;
  decision?: string | null;
  decided_at?: string | null;
}

export interface InvoiceApprovalApprover {
  login: string;
  name: string;
  initials?: string | null;
  kuerzel?: string | null;
}

/** Kürzel an der TK-Nummer ist der Login (flb → FLB), nicht die Namensinitialen (FB). */
export function approverKuerzel(person: Pick<InvoiceApprovalApprover, "login" | "kuerzel" | "initials">): string {
  const login = (String(person.login || "").split("@")[0] || "").trim();
  if (/^[A-Za-z0-9]{2,6}$/.test(login)) return login.toUpperCase();
  const explicit = String(person.kuerzel || "").trim();
  if (/^[A-Za-z0-9]{2,6}$/.test(explicit)) return explicit.toUpperCase();
  const initials = String(person.initials || "").trim();
  if (/^[A-Za-z0-9]{2,6}$/.test(initials) && initials.toUpperCase() === login.toUpperCase()) {
    return initials.toUpperCase();
  }
  return "";
}

export interface InvoiceApprovalHeader {
  invoice_number?: string | null;
  invoice_date?: string | null;
  supplier_name?: string | null;
  total_net?: number | null;
  total_gross?: number | null;
  currency?: string | null;
  due_date?: string | null;
  supplier_iban?: string | null;
  payment_terms?: string | null;
  skonto?: { percent: number; until?: string | null } | null;
  four_eyes_required?: boolean;
}

export interface InvoiceApprovalPreview {
  id: string;
  name?: string | null;
  content_type?: string | null;
  kind: "pdf" | "image";
}

export interface InvoiceApprovalQueueItem {
  candidate_id: number;
  status: InvoiceApprovalStatus;
  assignee_login?: string | null;
  assignee_name?: string | null;
  second_assignee_name?: string | null;
  subject?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  received_at?: string | null;
  mail_status?: string | null;
  header: InvoiceApprovalHeader;
  preview?: InvoiceApprovalPreview | null;
  project_count: number;
  filed_count: number;
  missing_project: boolean;
  price_deviation_count: number;
  unmatched_count: number;
  duplicate: boolean;
}

export interface InvoiceApprovalProject {
  auftrag_id?: number | null;
  order_id?: number | null;
  auftragsnummer?: string | null;
  jahr?: number | null;
  beschreibung?: string | null;
  bestellnummer?: string | null;
  kunde?: string | null;
  supplier?: string | null;
  match_kind?: string | null;
  match_confidence?: string | null;
}

export interface InvoiceApprovalOrderProject {
  auftrag_id?: number | null;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
}

/** Bestellung, deren TK-Nummer an dieser Rechnung erkannt wurde. */
export interface InvoiceApprovalOrder {
  order_id: number;
  supplier_name?: string | null;
  created_at?: string | null;
  geliefert_at?: string | null;
  cancelled_at?: string | null;
  bestellnummern: string[];
  auftraege: InvoiceApprovalOrderProject[];
  delivered: boolean;
  /** Nachladen des Lieferstatus ist fehlgeschlagen. */
  lookup_failed?: boolean;
}

export function orderLabel(order: InvoiceApprovalOrder): string {
  return order.bestellnummern.filter(Boolean).join(", ") || (order.order_id ? `Bestellung #${order.order_id}` : "Bestellung");
}

/** Gruppiert erkannte Projekt-Treffer zu einer Karte je Bestellung. */
export function ordersFromProjects(projects: InvoiceApprovalProject[]): InvoiceApprovalOrder[] {
  const grouped = new Map<string, InvoiceApprovalOrder>();
  for (const project of projects) {
    const number = String(project.bestellnummer || "").trim();
    const orderId = Number(project.order_id);
    const hasOrder = Number.isFinite(orderId) && orderId > 0;
    if (project.match_kind === "projekt" && !number && !hasOrder) continue;
    if (!hasOrder && !number) continue;
    const key = hasOrder ? `id:${orderId}` : `tk:${number.toUpperCase()}`;
    let bucket = grouped.get(key);
    if (!bucket) {
      bucket = {
        order_id: hasOrder ? orderId : 0,
        supplier_name: project.supplier || null,
        bestellnummern: [],
        auftraege: [],
        delivered: false,
      };
      grouped.set(key, bucket);
    }
    if (number && !bucket.bestellnummern.some((item) => item.toUpperCase() === number.toUpperCase())) {
      bucket.bestellnummern.push(number);
    }
    if (!bucket.supplier_name && project.supplier) bucket.supplier_name = project.supplier;
    const knownProject = bucket.auftraege.some(
      (item) => item.auftrag_id === project.auftrag_id && item.auftragsnummer === project.auftragsnummer,
    );
    if (!knownProject && (project.auftrag_id || project.auftragsnummer)) {
      bucket.auftraege.push({
        auftrag_id: project.auftrag_id,
        auftragsnummer: project.auftragsnummer,
        beschreibung: project.beschreibung,
      });
    }
  }
  return [...grouped.values()].sort((a, b) => orderLabel(a).localeCompare(orderLabel(b), "de"));
}

export function undeliveredOrders(orders: InvoiceApprovalOrder[]): InvoiceApprovalOrder[] {
  return orders.filter((order) => !order.lookup_failed && !order.delivered && !order.cancelled_at);
}

export function deliveryWarning(orders: InvoiceApprovalOrder[]): string | null {
  const open = undeliveredOrders(orders);
  const only = open[0];
  if (!only) return null;
  if (open.length === 1) {
    return `Die Bestellung ${orderLabel(only)} ist noch nicht als geliefert markiert.`;
  }
  return `${open.length} erkannte Bestellungen sind noch nicht als geliefert markiert.`;
}

export interface InvoiceApprovalFiledPath {
  bucket?: string | null;
  path?: string | null;
  web_url?: string | null;
  filename?: string | null;
  auftragsnummer?: string | null;
}

export interface InvoiceApprovalPrice {
  state: "match" | "higher" | "lower" | "unmatched" | "no_ek";
  delta?: number | null;
  percent?: number | null;
  stamm_ek?: number | null;
  invoice_price?: number | null;
}

export interface InvoiceApprovalLine {
  id: number;
  line_index: number;
  bezeichnung: string;
  menge?: number | null;
  einheit?: string | null;
  einzelpreis_netto?: number | null;
  zeilen_netto?: number | null;
  seller_article_nr?: string | null;
  ean?: string | null;
  stamm_artikel_id?: number | null;
  stamm_name?: string | null;
  stamm_nr?: string | null;
  match_method?: string | null;
  match_confidence?: string | null;
  price: InvoiceApprovalPrice;
}

export interface InvoiceApprovalDuplicate {
  candidate_id: number;
  subject?: string | null;
  invoice_number?: string | null;
  supplier_name?: string | null;
  status?: string | null;
  received_at?: string | null;
}

export interface InvoiceApprovalEvent {
  actor_login?: string | null;
  actor_name?: string | null;
  action: string;
  note?: string | null;
  created_at?: string | null;
}

export interface InvoiceApprovalPermissions {
  can_decide: boolean;
  can_comment: boolean;
  can_edit_header: boolean;
  as_second?: boolean;
  as_substitute?: boolean;
  reason?: string | null;
}

export interface InvoiceApprovalAttachment {
  id?: string | null;
  name?: string | null;
  content_type?: string | null;
  size?: number | null;
}

export interface InvoiceApprovalDetail {
  candidate_id: number;
  subject?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  received_at?: string | null;
  mail_status?: string | null;
  attachments: InvoiceApprovalAttachment[];
  approval: InvoiceApprovalSummary;
  permissions: InvoiceApprovalPermissions;
  header: InvoiceApprovalHeader;
  four_eyes_threshold: number;
  projects: InvoiceApprovalProject[];
  orders?: InvoiceApprovalOrder[];
  filed_paths: InvoiceApprovalFiledPath[];
  missing_project: boolean;
  lines: InvoiceApprovalLine[];
  line_count: number;
  price_deviation_count: number;
  unmatched_count: number;
  duplicates: InvoiceApprovalDuplicate[];
  overdue: boolean;
  events: InvoiceApprovalEvent[];
}

export interface InvoiceApprovalHeaderInput {
  invoice_number?: string | null;
  invoice_date?: string | null;
  supplier_name?: string | null;
  total_net?: number | null;
  total_gross?: number | null;
  currency?: string | null;
  due_date?: string | null;
  supplier_iban?: string | null;
  payment_terms?: string | null;
}

export interface InvoiceApprovalDecideInput {
  action: "freigeben" | "in_klaerung" | "ablehnen" | "kommentar";
  note?: string | null;
  second_assignee_login?: string | null;
  acknowledge_deviations?: boolean;
  acknowledge_duplicate?: boolean;
  acknowledge_missing_project?: boolean;
  header?: InvoiceApprovalHeaderInput | null;
}

export function approvalStatusLabel(status: InvoiceApprovalStatus | string): string {
  switch (status) {
    case "offen":
      return "Offen";
    case "in_klaerung":
      return "In Klärung";
    case "warte_zweite_freigabe":
      return "Zweite Freigabe";
    case "freigegeben":
      return "Freigegeben";
    case "abgelehnt":
      return "Abgelehnt";
    default:
      return status;
  }
}

export function approvalStatusClass(status: InvoiceApprovalStatus | string): string {
  switch (status) {
    case "offen":
      return "bg-status-orange/15 text-status-orange";
    case "in_klaerung":
      return "bg-accent/15 text-accent";
    case "warte_zweite_freigabe":
      return "bg-status-orange/15 text-status-orange";
    case "freigegeben":
      return "bg-status-green/15 text-status-green";
    case "abgelehnt":
      return "bg-status-red/15 text-status-red";
    default:
      return "bg-label-secondary/15 text-label-secondary";
  }
}

export function formatMoney(value: number | null | undefined, currency = "EUR"): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const code = /^[A-Z]{3}$/i.test(currency || "") ? currency.toUpperCase() : "EUR";
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: code }).format(Number(value));
  } catch {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(value));
  }
}

export function formatDay(value: string | null | undefined): string {
  if (!value) return "—";
  const match = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  return `${match[3]}.${match[2]}.${match[1]}`;
}

export function formatIban(value: string | null | undefined): string {
  const compact = String(value || "").replace(/\s+/g, "").toUpperCase();
  if (!compact) return "—";
  return compact.replace(/(.{4})/g, "$1 ").trim();
}

export function matchMethodLabel(method: string | null | undefined): string {
  switch (method) {
    case "ean":
      return "EAN";
    case "lieferanten_artnr":
      return "Lieferanten-Art.-Nr.";
    case "hero_nr":
      return "Artikelnummer";
    case "name_fuzzy":
      return "Bezeichnung";
    default:
      return "kein Treffer";
  }
}

export function eventActionLabel(action: string): string {
  switch (action) {
    case "assigned":
      return "Zugewiesen";
    case "reassigned":
      return "Neu zugewiesen";
    case "in_klaerung":
      return "In Klärung gesetzt";
    case "erste_freigabe":
      return "Erste Freigabe";
    case "teilfreigabe":
      return "Teilfreigabe";
    case "freigegeben":
      return "Freigegeben";
    case "abgelehnt":
      return "Abgelehnt";
    case "kommentar":
      return "Kommentar";
    case "kopfdaten":
      return "Kopfdaten gespeichert";
    default:
      return action;
  }
}

export async function listInvoiceApprovalApprovers(): Promise<{
  four_eyes_threshold: number;
  items: InvoiceApprovalApprover[];
}> {
  return api.get("/invoice-approval/approvers");
}

export async function fetchInvoiceApprovalOpenCount(): Promise<number> {
  const res = await api.get<{ count: number }>("/invoice-approval/open-count");
  return Number(res.count || 0);
}

export async function listInvoiceApprovals(scope: InvoiceApprovalScope): Promise<{
  scope: InvoiceApprovalScope;
  four_eyes_threshold: number;
  items: InvoiceApprovalQueueItem[];
}> {
  return api.get(`/invoice-approval?scope=${encodeURIComponent(scope)}`);
}

export async function getInvoiceApproval(candidateId: number): Promise<InvoiceApprovalDetail> {
  return api.get(`/invoice-approval/${candidateId}`);
}

export async function assignInvoiceApproval(
  candidateId: number,
  assigneeLogins: string[],
): Promise<InvoiceApprovalDetail> {
  const logins = assigneeLogins.map((login) => login.trim()).filter(Boolean);
  return api.post(`/invoice-approval/${candidateId}/assign`, {
    assignee_login: logins[0] || "",
    assignee_logins: logins,
  });
}

export async function saveInvoiceApprovalHeader(
  candidateId: number,
  header: InvoiceApprovalHeaderInput,
): Promise<InvoiceApprovalDetail> {
  return api.put(`/invoice-approval/${candidateId}/header`, header);
}

export async function decideInvoiceApproval(
  candidateId: number,
  payload: InvoiceApprovalDecideInput,
): Promise<InvoiceApprovalDetail> {
  return api.post(`/invoice-approval/${candidateId}/decide`, payload);
}
